import { useState, useEffect } from 'react';
import mapFieldsToState from '../lib/mapFieldsToState';
import getVariantsFormFields from '../lib/getVariantsFormFields';
import { getTicketGroupsFormState } from '../lib/getTicketGroupsFormState';
import getOrderBumpsFormState from '../lib/getOrderBumpsFormState';
import settleOrderBumps from '../lib/settleOrderBumps';
import { isEqual } from 'lodash';
import { formatPriceInput } from '../services/formatPrice';
import usePrevious from '../hooks/usePrevious';
import resolvePwywQueryAmount from '../lib/resolvePwywQueryAmount';
import { draftGuestRows } from '../lib/guests/checkoutGuests';

export default function useCheckoutForm({
  checkout,
  product,
  query,
  countryCode,
  onPayWhatYouWantPriceChange,
  draftCheckout,
  isPreview,
  pwywPriceReference,
  isMultiPriceProduct = false,
  selectedPriceId,
}) {
  // Minor units from the URL: `${reference}_pwyw` on a multi-price product, the
  // unprefixed `pwyw` on a single-price one. Falls back to the recovered draft.
  const pwywFromQuery = () => {
    const fromQuery = resolvePwywQueryAmount({
      query,
      reference: pwywPriceReference,
      isMultiPrice: isMultiPriceProduct,
    });
    if (fromQuery != null) return Number(fromQuery / 100);
    return draftCheckout?.payWhatYouWantPrice
      ? Number(draftCheckout.payWhatYouWantPrice / 100)
      : null;
  };

  const [form, setForm] = useState(() => {
    const ticketGroups =
      checkout.ticketGroups &&
      getTicketGroupsFormState(checkout.ticketGroups, query, draftCheckout);

    return {
      discountCode: '',
      fields:
        checkout.fields && mapFieldsToState(checkout.fields, query, countryCode, draftCheckout),
      variants: product.variants && getVariantsFormFields(product.variants, query, draftCheckout),
      ticketGroups,
      guests: draftGuestRows(draftCheckout),
      // No draft restore on purpose: abandonment drafts are bump-free (D3).
      // Settled here rather than by OrderBumps after mount, so the server
      // renders preselected bumps already ticked and their dependents in place.
      orderBumps: settleOrderBumps(
        {},
        {
          page: checkout,
          product,
          selectedPrice:
            (product?.prices ?? []).find(price => String(price._id) === String(selectedPriceId)) ??
            null,
          ticketGroups,
        },
      ),
      orderBumpAmounts: {},
      payWhatYouWant: pwywFromQuery(),
      coverProcessingFee: undefined,
    };
  });

  const previousQuery = usePrevious(query);
  const previousPwywReference = usePrevious(pwywPriceReference);

  // A price switch changes the PWYW lookup key, so re-seed that amount alone. It must
  // NOT go through the query effect below: that one rebuilds fields/variants/tickets
  // from scratch, which would wipe everything the customer has typed each time they
  // change price.
  useEffect(() => {
    if (pwywPriceReference === previousPwywReference) return;
    setForm(prevForm => ({ ...prevForm, payWhatYouWant: pwywFromQuery() }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pwywPriceReference]);

  // update the form state when the query changes
  // used to update fields, variants and pay what you want price
  useEffect(() => {
    if (isEqual(query, previousQuery)) return;

    setForm(prevForm => ({
      ...prevForm,
      fields:
        checkout.fields && mapFieldsToState(checkout.fields, query, countryCode, draftCheckout),
      variants: product.variants && getVariantsFormFields(product.variants, query, draftCheckout),
      ticketGroups:
        checkout.ticketGroups &&
        getTicketGroupsFormState(checkout.ticketGroups, query, draftCheckout),
      payWhatYouWant: pwywFromQuery(),
    }));
  }, [query]);

  /**
   * PREVIEW USE EFFECTS BEGIN!
   *
   * In our dashboard we have a preview component that allows us to directly render store (starting with the translation and then checkout state providers)
   * In the dashboard, merchants can update their store and the changes are reflected instantly. To keep the fields, variants and tickets reactive we
   * set the state of the form each time these change. This will reset the values in the respective entity. E.g. if the dashboard sends an update
   * to update fields, it will clear all values in fields (rerendered). This is why we use an !isPreview check to guard against this clearing of values
   * in a production environment.
   *
   * Removing/altering these useEffects has multiple side effects - it can break the dashboard being reactive, it can create the dashboard to error out
   * it can clear the data in the forms and more. If you change these please be extra cautious.
   *
   * TL;DR be careful!
   */
  useEffect(() => {
    if (!isPreview) return;

    setForm(prevForm => ({
      ...prevForm,
      fields:
        checkout.fields && mapFieldsToState(checkout.fields, query, countryCode, draftCheckout),
    }));
  }, [checkout.fields, isPreview]);

  useEffect(() => {
    if (!isPreview) return;

    setForm(prevForm => ({
      ...prevForm,
      variants: product.variants && getVariantsFormFields(product.variants, query, draftCheckout),
    }));
  }, [product.variants, isPreview]);

  useEffect(() => {
    if (!isPreview) return;

    setForm(prevForm => ({
      ...prevForm,
      ticketGroups:
        checkout.ticketGroups &&
        getTicketGroupsFormState(checkout.ticketGroups, query, draftCheckout),
    }));
  }, [checkout.ticketGroups, isPreview]);

  useEffect(() => {
    if (!isPreview) return;

    setForm(prevForm => ({
      ...prevForm,
      // Rebuilt from the placements' preselect flags, NOT emptied — the same
      // shape as the variants line above. OrderBumps seeds preselect in a child
      // effect, which React runs before this one, so emptying here silently
      // wiped every preselect the moment the seller saved. Anything not
      // actually eligible is pruned by that same child effect.
      orderBumps: getOrderBumpsFormState(checkout.orderBumps),
      orderBumpAmounts: {},
    }));
  }, [checkout.orderBumps, isPreview]);
  /**
   * PREVIEW USE EFFECTS END!
   */

  // update pay what you want price when form changes
  useEffect(() => {
    onPayWhatYouWantPriceChange?.(formatPriceInput(form.payWhatYouWant, product.currency));
  }, [form.payWhatYouWant, onPayWhatYouWantPriceChange, product.currency]);

  return {
    form,
    setForm,
  };
}
